import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { RoutineRunDTO } from "@app-factory/shared";
import { api, ApiRequestError } from "@/lib/api";
import { DataTable, type Column } from "@/design/DataTable";
import { cs } from "@/i18n/cs";
import {
  Btn,
  Chip,
  DetailHeader,
  EmptyState,
  Panel,
} from "@/design/primitives";
import { relativeTime } from "@/lib/format";
import { useAuth } from "@/auth/auth";
import { useRealtimeEvent } from "@/realtime/useRealtime";
import { nextRunLabel } from "./RoutinesPanel";

const OUTPUT_TONES: Record<string, { color: string; border: string }> = {
  TASK: {
    color: "var(--accent)",
    border: "color-mix(in srgb, var(--accent) 35%, transparent)",
  },
  PROPOSAL: {
    color: "var(--warning)",
    border: "color-mix(in srgb, var(--warning) 40%, transparent)",
  },
  DECISION: {
    color: "var(--warning)",
    border: "color-mix(in srgb, var(--warning) 40%, transparent)",
  },
  REPORT: { color: "var(--text-primary)", border: "var(--border-default)" },
};

/** Routine detail dle /Design: definice, instrukce, výstupy, běhy, autonomie. */
export function RoutineDetailPage() {
  const params = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const isOwner = session?.user.role === "OWNER";

  const query = useQuery({
    queryKey: ["routine", params.id],
    queryFn: () => api.routine(params.id),
    retry: (count, err) =>
      !(err instanceof ApiRequestError && err.status === 404) && count < 2,
  });
  useRealtimeEvent((m) => {
    if (m.type === "registry.changed" && m.area === "routines") {
      void queryClient.invalidateQueries({ queryKey: ["routine", params.id] });
    }
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["routine", params.id] });
    void queryClient.invalidateQueries({ queryKey: ["routines"] });
  };
  const pauseMutation = useMutation({
    mutationFn: (status: "ACTIVE" | "PAUSED") =>
      api.updateRoutine(params.id, { status }),
    onSuccess: invalidate,
  });
  const runNowMutation = useMutation({
    mutationFn: () => api.runRoutineNow(params.id),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteRoutine(params.id),
    onSuccess: () => void navigate({ to: "/workers" }),
  });

  if (query.error instanceof ApiRequestError) {
    return (
      <Panel className="mx-auto mt-16 max-w-md p-6 text-center">
        <EmptyState title={cs.routine.notFound} />
        <Link to="/workers" className="text-[12px]">
          {cs.routine.back}
        </Link>
      </Panel>
    );
  }
  const routine = query.data?.routine;
  const runs = query.data?.runs ?? [];
  if (!routine) return null;

  const active = routine.status === "ACTIVE";

  return (
    <div className="af-animate-fade">
      <DetailHeader
        back={
          <Link to="/workers" style={{ color: "inherit" }}>
            {cs.routine.back}
          </Link>
        }
        title={routine.name}
        meta={
          <span data-testid="routine-status">
            <Chip tone={active ? "green" : "amber"}>
              {active ? cs.routine.active : cs.routine.paused}
            </Chip>
          </span>
        }
        right={
          isOwner ? (
            <>
              <Btn
                tone={active ? "amber" : "green"}
                onClick={() =>
                  pauseMutation.mutate(active ? "PAUSED" : "ACTIVE")
                }
                data-testid="routine-toggle"
              >
                {active ? cs.routine.pause : cs.routine.resume}
              </Btn>
              {active ? (
                <Btn
                  onClick={() => runNowMutation.mutate()}
                  disabled={runNowMutation.isPending}
                  data-testid="routine-run-now"
                >
                  {cs.routine.runNow}
                </Btn>
              ) : null}
              <button
                type="button"
                className="cursor-pointer px-2 text-[11px]"
                style={{ color: "var(--critical)" }}
                onClick={() => {
                  if (confirm(`Smazat routine ${routine.name}?`))
                    deleteMutation.mutate();
                }}
              >
                ✕ {cs.routine.delete}
              </button>
            </>
          ) : null
        }
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_1.3fr]">
        <div className="flex flex-col gap-3.5">
          <Panel className="p-4" data-testid="routine-definition">
            <div className="af-label mb-2.5" style={{ color: "var(--accent)" }}>
              {cs.routine.definition}
            </div>
            <DefRow
              k={cs.routine.schedule}
              v={routine.scheduleLabel}
              color="var(--text-primary)"
            />
            <DefRow
              k={cs.routine.department}
              v={routine.departmentName ?? "—"}
              color="var(--accent)"
            />
            <DefRow
              k={cs.routine.scope}
              v={
                routine.projects.length
                  ? routine.projects.map((p) => p.name).join(" · ")
                  : cs.workers.routineAllProjects
              }
            />
            <DefRow
              k={cs.routine.capabilities}
              v={routine.capabilities.join(", ") || "—"}
            />
            <DefRow k={cs.routine.model} v={routine.modelHint ?? "—"} />
            <DefRow k={cs.workers.routineNext} v={nextRunLabel(routine)} />
          </Panel>

          <Panel className="p-4">
            <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
              {cs.routine.instructions}
            </div>
            <div
              className="pl-3 text-[12px] italic leading-relaxed"
              style={{
                borderLeft:
                  "2px solid color-mix(in srgb, var(--accent) 15%, transparent)",
                color: "var(--text-primary)",
              }}
            >
              {routine.instructions}
            </div>
          </Panel>

          <Panel className="p-4">
            <div className="af-label mb-2.5" style={{ color: "var(--accent)" }}>
              {cs.routine.outputs}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {routine.outputs.map((o) => (
                <span
                  key={o}
                  className="px-2.5 py-1 text-[11px] tracking-[0.08em]"
                  style={{
                    borderRadius: "var(--radius-box)",
                    color: OUTPUT_TONES[o]?.color,
                    border: `1px solid ${OUTPUT_TONES[o]?.border}`,
                  }}
                >
                  {o}
                </span>
              ))}
            </div>
          </Panel>
        </div>

        <div className="flex flex-col gap-3.5">
          <Panel className="p-4" data-testid="routine-runs">
            <div className="flex flex-wrap justify-between gap-x-2 gap-y-1">
              <span className="af-label" style={{ color: "var(--accent)" }}>
                {cs.routine.runs}
              </span>
              <span className="af-label-dim">
                {cs.routine.tokenUsage(
                  routine.tokensTotal
                    ? `${Math.round(routine.tokensTotal / 1000)}k`
                    : "—",
                )}
              </span>
            </div>
            <div className="mt-2.5">
              <DataTable
                columns={
                  [
                    {
                      key: "time",
                      label: cs.routine.runTime,
                      width: "100px",
                      cell: (run) => (
                        <span style={{ color: "var(--text-tertiary)" }}>
                          {relativeTime(run.startedAt)}
                        </span>
                      ),
                    },
                    {
                      key: "worker",
                      label: cs.routine.runWorker,
                      width: "minmax(110px, 1fr)",
                      primary: true,
                      cell: (run) => run.workerName ?? "—",
                    },
                    {
                      key: "model",
                      label: cs.routine.runModel,
                      width: "130px",
                      cell: (run) => run.models[0] ?? "—",
                    },
                    {
                      key: "tokens",
                      label: cs.routine.runTokens,
                      width: "80px",
                      cell: (run) =>
                        run.tokensTotal != null
                          ? `${(run.tokensTotal / 1000).toFixed(1)}k`
                          : "—",
                    },
                    {
                      key: "out",
                      label: cs.routine.runOut,
                      width: "minmax(160px, 1.4fr)",
                      cell: (run) => <RunOutcome run={run} />,
                    },
                  ] satisfies Column<RoutineRunDTO>[]
                }
                rows={runs}
                rowKey={(run) => run.id}
                rowTestId={() => "routine-run-row"}
                stackBelow={1024}
                empty={
                  <div
                    className="py-2 text-[12px]"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    {cs.routine.noRuns}
                  </div>
                }
              />
            </div>
          </Panel>

          <Panel className="p-4">
            <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
              {cs.routine.autonomy}
            </div>
            <div
              className="text-[12px] leading-relaxed"
              style={{ color: "var(--text-secondary)" }}
            >
              {routine.autonomy || cs.routine.autonomyEmpty}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function DefRow({ k, v, color }: { k: string; v: string; color?: string }) {
  return (
    <div
      className="flex gap-3 py-1.5 text-[12px]"
      style={{
        borderTop:
          "1px solid color-mix(in srgb, var(--accent) 5%, transparent)",
      }}
    >
      <span
        className="w-[110px] flex-none"
        style={{ color: "var(--text-tertiary)" }}
      >
        {k}
      </span>
      <span
        className="min-w-0 break-words"
        style={{ color: color ?? "var(--text-primary)" }}
      >
        {v}
      </span>
    </div>
  );
}

/** Výsledek běhu ve sloupci VÝSTUP: shrnutí, štítky výstupů a report pod nimi. */
function RunOutcome({ run }: { run: RoutineRunDTO }) {
  const color =
    run.status === "RUNNING"
      ? "var(--accent)"
      : run.status === "FAILED"
        ? "var(--critical)"
        : run.outputRefs.length
          ? "var(--text-secondary)"
          : "var(--positive)";
  return (
    <span className="block">
      <span style={{ color }}>
        {run.status === "RUNNING"
          ? cs.workers.routineRunning
          : run.summary || run.status}
      </span>
      {run.outputRefs.length > 0 ? (
        <span className="mt-1.5 flex flex-wrap gap-1.5">
          {run.outputRefs.map((ref, i) => (
            <span
              key={i}
              className="af-mono px-2 py-0.5 text-[10px]"
              style={{
                borderRadius: "var(--radius-pill)",
                color: OUTPUT_TONES[ref.kind]?.color,
                border: `1px solid ${OUTPUT_TONES[ref.kind]?.border}`,
              }}
            >
              {ref.kind} · {ref.label}
            </span>
          ))}
        </span>
      ) : null}
      {run.report ? (
        <span
          className="mt-1.5 block break-words pl-2.5 text-[12px] leading-relaxed"
          style={{
            borderLeft: "2px solid var(--border-default)",
            color: "var(--text-tertiary)",
          }}
        >
          {run.report}
        </span>
      ) : null}
    </span>
  );
}
